/**
 * QuizSessionManager - Manages the quiz session data and persistence
 * Handles loading, saving, and updating the quiz progress
 */
class QuizSessionManager {
  constructor() {
    this.storageKey = 'naturaw_quiz_session';
    this.session = this.loadSession();
    this.eventBus = window.QuizEventBus;
  }

  /**
   * Get default session structure with empty values
   * @returns {Object} Default session object
   */
  getDefaultSession() {
    return {
      quiz_id: `quiz_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      current_step: 1,
      started_at: new Date().toISOString(),
      
      dogs: [],
      editing_dog_id: null,
      
      feeding_style: '',
      avoided_ingredients: [],
      
      email_address: '',
      email_preferences: {
        recommendations: true,
        discounts: true,
        feeding_tips: true
      },
      
      selected_pack_size: '',
      pack_details: null,
      
      selected_recipes: [],
      total_meals_added: 0,
      
      selected_treats: [],
      selected_supplements: [],
      
      subscription_frequency: 'monthly',
      subscription_discount: '',
      selected_selling_plan_id: null,
      
      completed_steps: [],
      is_complete: false,
      validation_errors: {}
    };
  }

  /**
   * Load session from storage or create new one
   * @returns {Object} Current session data
   */
  loadSession() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const session = { ...this.getDefaultSession(), ...parsed };
        
        if (this.eventBus) {
          this.eventBus.publish('session:loaded', session);
        }
        
        return session;
      }
    } catch (e) {
      console.warn('[QuizSession] Failed to load session:', e);
    }
    
    const newSession = this.getDefaultSession();
    
    if (this.eventBus) {
      this.eventBus.publish('session:created', newSession);
    }
    
    return newSession;
  }

  /**
   * Save session to storage
   * @param {Object} session - Session data to save
   */
  saveSession(session = this.session) {
    try {
      session.last_updated = new Date().toISOString();
      localStorage.setItem(this.storageKey, JSON.stringify(session));
      this.session = session;
      
      if (this.eventBus) {
        this.eventBus.publish('session:saved', session);
      }
    } catch (e) {
      console.error('[QuizSession] Failed to save session:', e);
      
      if (this.eventBus) {
        this.eventBus.publish('session:save_error', e);
      }
    }
  }

  /**
   * Get the current session
   * @returns {Object} Current session data
   */
  getSession() {
    return this.session;
  }

  /**
   * Update session with new data
   * @param {Object} updates - Data to update in session
   * @returns {Object} Updated session
   */
  updateSession(updates) {
    const previousSession = { ...this.session };
    this.session = { ...this.session, ...updates };
    this.saveSession();
    
    if (this.eventBus) {
      this.eventBus.publish('session:updated', {
        previous: previousSession,
        current: this.session,
        changes: updates
      });
    }
    
    return this.session;
  }

  /**
   * Update a specific field in the session
   * @param {string} field - Field name to update
   * @param {any} value - New value
   * @returns {Object} Updated session
   */
  updateField(field, value) {
    const updates = {};
    updates[field] = value;
    return this.updateSession(updates);
  }

  updateStepConfig(steps) {
    this.session.step_config = steps;
    this.saveSession();
    return this.session;
  }

  /**
   * Set validation error for a field
   * @param {string} field - Field name with error
   * @param {string} message - Error message
   */
  setValidationError(field, message) {
    this.session.validation_errors = this.session.validation_errors || {};
    this.session.validation_errors[field] = message;
    this.saveSession();
    
    if (this.eventBus) {
      this.eventBus.publish('validation:error', {
        field,
        message,
        errors: this.session.validation_errors
      });
    }
  }

  /**
   * Clear all validation errors
   */
  clearValidationErrors() {
    this.session.validation_errors = {};
    this.saveSession();
    
    if (this.eventBus) {
      this.eventBus.publish('validation:cleared', {});
    }
  }

  /**
   * Mark a step as completed
   * @param {number} step - Step number to mark completed
   */
  markStepCompleted(step) {
    if (!this.session.completed_steps) {
      this.session.completed_steps = [];
    }
    
    if (!this.session.completed_steps.includes(step)) {
      this.session.completed_steps.push(step);
      this.saveSession();
      
      if (this.eventBus) {
        this.eventBus.publish('step:completed', {
          step,
          completed_steps: this.session.completed_steps
        });
      }
    }
  }

  /**
   * Check if a step is completed
   * @param {number} step - Step number to check
   * @returns {boolean} Whether step is completed
   */
  isStepCompleted(step) {
    return this.session.completed_steps && this.session.completed_steps.includes(step);
  }

  /**
   * Navigate to a specific step
   * @param {number} step - Step number to navigate to
   */
  navigateToStep(step) {
    const previousStep = this.session.current_step;
    this.session.current_step = step;
    this.saveSession();
    
    if (this.eventBus) {
      this.eventBus.publish('step:change', {
        previous: previousStep,
        current: step
      });
    }
  }

  /**
   * Move to the next step
   */
  nextStep() {
    this.navigateToStep(this.session.current_step + 1);
  }

  /**
   * Move to the previous step
   */
  prevStep() {
    if (this.session.current_step > 1) {
      this.navigateToStep(this.session.current_step - 1);
    }
  }

  /**
   * Clear the session completely
   */
  clearSession() {
    localStorage.removeItem(this.storageKey);
    this.session = this.getDefaultSession();
    
    if (this.eventBus) {
      this.eventBus.publish('session:cleared', {});
    }
  }
}

// Create and export the session manager
window.QuizSessionManager = QuizSessionManager; 